// re.rejouis.fr — client des Netlify Functions (aucun secret ici).
import { sessionToken } from './auth.js';
import { parcoursPosthog } from './posthog.js';

// Passe par /api/* (proxy Netlify) pour bénéficier du rate-limiting anti-DoS
// défini dans netlify.toml (cf. [redirects.rate_limit]).
const BASE = '/api';

async function get(path) {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (e) { /* ignore */ }
    throw Object.assign(new Error(detail || `Erreur ${res.status}`), { status: res.status });
  }
  return res.json();
}

/** Étiquettes d'envoi libres (/envoi/<code>) : infos | creer | pdf, cf. netlify/functions/envoi.js. */
export function envoiLibre(action, code, champs = {}) {
  return post('/envoi', { action, code, ...champs });
}

/** Recherche catalogue (produits éligibles filtrés à la source). */
export function rechercheCatalogue(q) {
  return get(`/catalog?q=${encodeURIComponent(q)}`);
}

/** Un produit par handle (deep-link /reprendre/<handle>, écran état). */
export function produitParHandle(handle) {
  return get(`/catalog?handle=${encodeURIComponent(handle)}`);
}

/** Modèles populaires à reprendre (collection bestsellers, filtrée éligible). */
export function modelesPopulaires() {
  return get('/catalog?featured=1');
}

/** Option virement (rachat en euros) proposable ce mois-ci ? → { disponible, reste } */
export function disponibiliteVirement() {
  return get('/virement');
}

/** Modèles recherchés (Prio) : crédit boosté temporairement, posés au lab. */
export function modelesRecherches() {
  return get('/catalog?wanted=1');
}

/** Offres du moment : modèles boostés et modèles repris en virement (page /offres). */
export function offresDuMoment() {
  return get('/catalog?offres=1');
}

/** Produits éligibles portant ce code-barres (scan de la boîte). → { trouves, count, produits } */
export function produitsParEan(ean) {
  return get(`/catalog?ean=${encodeURIComponent(ean)}`);
}

async function post(path, payload) {
  const headers = { 'Content-Type': 'application/json' };
  const token = sessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (e) { /* ignore */ }
    throw Object.assign(new Error(detail || `Erreur ${res.status}`), { status: res.status });
  }
  return res.json();
}

/** Identification par photo (lab.rejouis.fr via la fonction scan). */
export function identifierParPhoto(imageBase64, mime) {
  return post('/scan', { action: 'identify', image: imageBase64, mime });
}

/**
 * Retour du client sur une identification photo (apprentissage de l'outil) :
 * verdict "confirme" (c'est bien son jouet) ou "refuse" (ce n'est pas lui).
 */
export function confirmerScan(imageBase64, productId, score, verdict = 'confirme') {
  return post('/scan', { action: 'ingest', image: imageBase64, productId, score, verdict });
}

/** Soumet une demande de reprise → écrit Demandes + Lignes dans Airtable. */
export function soumettreDemande({ client, lignes, type = 'standard', questionnaire = null, virement = null }) {
  return post('/demande', { client, lignes, type, questionnaire, virement, parcours: parcoursPosthog() }).then((r) => { invaliderDemandes(); return r; });
}

/**
 * Réponse du client sur sa demande (suivi) : "accepter_lot" | "retirer_lot" | "annuler"
 * | "etiquette_pdf" (le relais n'accepte pas le QR code → étiquette PDF à imprimer).
 * → { ok, statut, labelUrl? } — l'étiquette part quand le contenu du colis est figé.
 */
export function actionDemande(action, ref) {
  return post('/ma-demande', { action, ref }).then((r) => { invaliderDemandes(); return r; });
}

/** Retours après achat : commandes expédiées du client connecté (articles retournables). */
export async function chargerCommandesRetour() {
  const token = sessionToken();
  const res = await fetch(`${BASE}/retour`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `Erreur ${res.status}`), { status: res.status });
  return data.commandes || [];
}

/**
 * Crée le retour (Shopify) + QR code Mondial Relay. → { ref, calcul, etiquette }
 * Défectueux : { preuves, solution } en plus → réclamation sans renvoi. → { reclamation }
 */
export function creerRetour({ orderId, motif, note, lignes, preuves, solution, remplacements, relais }) {
  return post('/retour', { orderId, motif, note, lignes, preuves, solution, remplacements, relais });
}

/** Articles proposés en remplacement d'un article défectueux. → [{ variantId, titre, prix, image, identique }] */
export function equivalentsRetour(orderId, articleId) {
  return post('/retour', { action: 'equivalents', orderId, articleId }).then((r) => r.options || []);
}

/** Réponse à une demande de précisions sur une réclamation (texte + preuves déjà envoyées). */
export function repondreReclamation(orderId, reponse, preuves) {
  return post('/retour', { action: 'reclamation_complement', orderId, reponse, preuves });
}

/** Choix du client parmi les solutions proposées par l'équipe (remplacements : articleId → variantId). */
export function choisirReclamation(orderId, solution, remplacements, relais) {
  return post('/retour', { action: 'reclamation_choix', orderId, solution, remplacements, relais });
}

/** Le client annule sa réclamation en cours, avec une courte justification. */
export function annulerReclamation(orderId, justification) {
  return post('/retour', { action: 'reclamation_annuler', orderId, justification });
}

/** Points relais Mondial Relay autour d'un code postal. → [{ id, nom, adresse, cp, ville, pays, distance }] */
export function pointsRelaisRetour(cp, pays) {
  return post('/retour', { action: 'points_relais', cp, pays }).then((r) => r.points || []);
}

/**
 * Envoie les preuves d'un défaut (photo ou vidéo) directement chez Shopify (staged
 * uploads : pas de limite de 6 Mo comme via une fonction). → [{ url, mime }] à joindre à la
 * réclamation. onProgres(0..1) suit l'envoi, utile pour une vidéo.
 */
export async function envoyerPreuves(fichiers, onProgres = () => {}) {
  const { cibles } = await post('/retour', {
    action: 'preuves_cibles',
    fichiers: fichiers.map((f) => ({ nom: f.name, mime: f.type, taille: f.size })),
  });
  const total = fichiers.reduce((s, f) => s + f.size, 0) || 1;
  const envoye = fichiers.map(() => 0);
  await Promise.all(fichiers.map((f, i) => new Promise((resolve, reject) => {
    const form = new FormData();
    for (const p of cibles[i].parameters) form.append(p.name, p.value);
    form.append('file', f);
    // XHR plutôt que fetch : seul moyen de suivre la progression d'un envoi.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', cibles[i].url);
    xhr.upload.onprogress = (e) => { envoye[i] = e.loaded; onProgres(envoye.reduce((s, n) => s + n, 0) / total); };
    xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error('Envoi du fichier refusé, réessaie.')));
    xhr.onerror = () => reject(new Error('Connexion interrompue pendant l’envoi, réessaie.'));
    xhr.send(form);
  })));
  return fichiers.map((f, i) => ({ url: cibles[i].resourceUrl, mime: f.type }));
}

/** Le relais n'accepte pas le QR code → étiquette PDF (nouvelle expédition). */
export function etiquettePdfRetour(orderId) {
  return post('/retour', { action: 'etiquette_pdf', orderId });
}

/** Soumet une demande de reprise de lot (dès 1 jouet, estimation manuelle). */
export function soumettreLot({ email, shopify_customer_id, lot, photos }) {
  return post('/lot', { email, shopify_customer_id, lot, photos, parcours: parcoursPosthog() }).then((r) => { invaliderDemandes(); return r; });
}

/** Soumet une demande de référencement d'un modèle absent du catalogue (lien de la fiche neuve). */
export function soumettreReference({ email, shopify_customer_id, modele, lien }) {
  return post('/reference', { email, shopify_customer_id, modele, lien });
}

/** Vérifie qu'un lien de fiche neuve ne correspond pas à un modèle déjà au catalogue. */
export function verifierLien(lien) {
  return post('/verifier-lien', { lien });
}

/** Crée une fiche provisoire depuis l'URL d'un produit absent du catalogue → { ref }. */
export function creerFicheProvisoire(lien) {
  return post('/fiche-provisoire', { lien });
}

/** État d'une fiche provisoire → { fiche, produit } (produit quand elle est prête). */
export async function ficheProvisoire(ref) {
  const headers = {};
  const token = sessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/fiche-provisoire?ref=${encodeURIComponent(ref)}`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `Erreur ${res.status}`), { status: res.status });
  return data;
}

/** Demandes de reprise du client connecté (suivi : étapes, bordereau, colis, crédit). */
export async function mesDemandes() {
  const token = sessionToken();
  const res = await fetch(`${BASE}/mes-demandes`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `Erreur ${res.status}`), { status: res.status });
  return data;
}

// Demandes du client, partagées entre « Mon compte » (liste réduite) et le suivi :
// un aller-retour Compte → Suivi ne relance pas la fonction (quota Netlify).
// Vidé à chaque nouvelle demande / lot et à la déconnexion.
const TTL_DEMANDES = 60 * 1000;
let cacheDemandes = null; // { demandes, at }

export function chargerDemandes({ force = false } = {}) {
  if (!force && cacheDemandes && Date.now() - cacheDemandes.at < TTL_DEMANDES) return Promise.resolve(cacheDemandes.demandes);
  return mesDemandes().then((r) => {
    cacheDemandes = { demandes: r.demandes || [], at: Date.now() };
    return cacheDemandes.demandes;
  });
}

export function invaliderDemandes() { cacheDemandes = null; }

/** Avis sur le nouvel espace de reprise (pop-up) : note 1-5 + commentaire facultatif. */
export function envoyerAvis({ note, commentaire, ecran }) {
  return post('/avis', { note, commentaire, ecran });
}
